<script setup lang="ts">
import { computed, ref } from "vue";
import type { EditionHit } from "@/types/edition";
import { useContentStore } from "@/stores/contentStore.ts";
import { getEdition, EditionInclude } from "@/repository/index.ts";
import {
  buildHighlightedSegments,
  sliceAroundFirstMatch,
} from "@/lib/textHighlighting";

const contentStore = useContentStore();
const isLoading = ref(false);

const props = defineProps<{
  hits: EditionHit[];
  query?: string;
}>();

const handleClick = async (h: EditionHit) => {
  isLoading.value = true;
  try {
    // Fetch full edition data with subscription info before setting it
    const response = await getEdition(h.edition.id, {
      include: [
        EditionInclude.EndUserEditionData,
        EditionInclude.EndUserSubscription,
      ],
    });
    contentStore.selectedEdition = response.data.data;
    contentStore.selectedEditionPage = h.page_number;
  } catch (err) {
    console.error("Failed to fetch edition:", err);
    // Fallback to the hit data if fetch fails
    contentStore.selectedEdition = h.edition;
    contentStore.selectedEditionPage = h.page_number;
  } finally {
    isLoading.value = false;
  }
};

/**
 * Memoized highlighted segments for each hit.
 * Computed property ensures segments are only recalculated when hits or query changes.
 * Uses a stable key (edition.id + page_number) to prevent cache invalidation on array reordering.
 */
const highlightedSegmentsMap = computed(() => {
  const map = new Map<string, ReturnType<typeof buildHighlightedSegments>>();
  for (const hit of props.hits) {
    const key = `${hit.edition.id}-${hit.page_number}`;
    const sliced = sliceAroundFirstMatch(hit.snippet, props.query);
    const segments = buildHighlightedSegments(sliced, props.query);
    map.set(key, segments);
  }
  return map;
});

const getHighlightedSegments = (hit: EditionHit) => {
  const key = `${hit.edition.id}-${hit.page_number}`;
  return highlightedSegmentsMap.value.get(key) ?? [];
};
</script>

<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <button
      v-for="h in props.hits"
      :key="`hit-${h.edition.id}-${h.page_number}`"
      class="flex gap-3 text-left hover:opacity-80 transition-opacity"
      @click="handleClick(h)"
    >
      <img
        class="w-20 h-28 object-cover rounded bg-surface-3"
        :src="h.edition.thumbnail_url || ''"
        :alt="h.edition.name"
      />

      <div class="min-w-0 h-28 flex flex-col justify-center">
        <div class="text-sm font-medium line-clamp-1">
          {{ h.edition.name }}
          • {{ $t("common.page") }} {{ h.page_number }}
        </div>
        <p
          class="mt-1 text-sm text-text-secondary line-clamp-2 snippet"
        >
          <template v-for="(seg, segIdx) in getHighlightedSegments(h)" :key="segIdx">
            <mark v-if="seg.highlight" class="hl">{{ seg.text }}</mark>
            <template v-else>{{ seg.text }}</template>
          </template>
        </p>
      </div>
    </button>
  </div>
</template>
